import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmtTs } from "../lib/format";
import type { ApiKeyRow } from "../lib/types";
import { Card, CardTitle, Pill } from "../components/Card";

interface FlagsPayload {
  flags: {
    tradingPaused: string | null;
    globalSessions: string | null;
    telegramMutedIndicators: string | null;
    realizedAnchorMs: string | null;
    flipCooldownSeconds: string | null;
    hours: string;
    [k: string]: string | null;
  };
}

interface KeysPayload {
  keys: ApiKeyRow[];
}

const SESSIONS = ["asia", "europe", "us"] as const;
type SessionKey = (typeof SESSIONS)[number];

export default function SystemPage() {
  const qc = useQueryClient();
  const flags = useQuery({
    queryKey: ["flags"],
    queryFn: () => api.get<FlagsPayload>("/admin/api/flags"),
  });
  const keys = useQuery({
    queryKey: ["keys"],
    queryFn: () => api.get<KeysPayload>("/admin/api/keys"),
  });

  const invalidateAll = (): void => {
    void qc.invalidateQueries({ queryKey: ["flags"] });
    void qc.invalidateQueries({ queryKey: ["keys"] });
    void qc.invalidateQueries({ queryKey: ["overview"] });
  };

  if (flags.isLoading || keys.isLoading)
    return <div className="text-muted">Loading…</div>;
  if (flags.error || keys.error || !flags.data || !keys.data)
    return (
      <div className="rounded-md border border-trading-down/40 bg-trading-down/10 px-4 py-3 text-trading-down">
        Failed: {(flags.error as Error | undefined)?.message ?? (keys.error as Error | undefined)?.message}
      </div>
    );

  const hours = JSON.parse(flags.data.flags.hours) as Record<string, number>;

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
        <h1 className="text-2xl font-bold text-on-dark sm:text-display-sm">System</h1>
        <span className="text-xs text-muted">Tradeable state · session windows · API keys</span>
      </header>

      <PauseCard
        paused={flags.data.flags.tradingPaused === "true"}
        onSaved={invalidateAll}
      />

      <GlobalSessionsCard
        current={flags.data.flags.globalSessions}
        onSaved={invalidateAll}
      />

      <SessionHoursCard hours={hours} onSaved={invalidateAll} />

      <FlipCooldownCard
        current={flags.data.flags.flipCooldownSeconds ?? ""}
        onSaved={invalidateAll}
      />

      <TelegramMutesCard
        current={flags.data.flags.telegramMutedIndicators ?? ""}
        onSaved={invalidateAll}
      />

      <ResetRealizedCard
        anchorMs={flags.data.flags.realizedAnchorMs ?? null}
        onSaved={invalidateAll}
      />

      <ApiKeysCard keys={keys.data.keys} onChanged={invalidateAll} />
    </div>
  );
}

function PauseCard({ paused, onSaved }: { paused: boolean; onSaved: () => void }) {
  const mutation = useMutation({
    mutationFn: (newValue: boolean) =>
      api.post("/admin/api/flags/tradingPaused", { value: newValue ? "true" : "false" }),
    onSuccess: onSaved,
  });
  return (
    <Card>
      <CardTitle right="affects every EA poll immediately">Global trading switch</CardTitle>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="text-lg font-semibold text-on-dark sm:text-title-md">
            {paused ? "Trading paused" : "Trading live"}
          </div>
          <p className="mt-1 text-sm text-muted">
            When paused, EAs receive empty `/api/signals` responses. TV webhooks still
            persist for audit but won't reach the EA.
          </p>
        </div>
        <button
          type="button"
          className={`self-start ${paused ? "btn-primary" : "btn-secondary"}`}
          onClick={() => mutation.mutate(!paused)}
          disabled={mutation.isPending}
        >
          {mutation.isPending
            ? "Saving…"
            : paused
              ? "Resume trading"
              : "Pause trading"}
        </button>
      </div>
    </Card>
  );
}

function GlobalSessionsCard({
  current,
  onSaved,
}: {
  current: string | null;
  onSaved: () => void;
}) {
  const [sessions, setSessions] = useState<SessionKey[]>(parseSessions(current));
  const [override, setOverride] = useState<boolean>(!!current && current.trim() !== "");

  useEffect(() => {
    setOverride(!!current && current.trim() !== "");
    setSessions(parseSessions(current));
  }, [current]);

  const mutation = useMutation({
    mutationFn: () =>
      api.post("/admin/api/flags/globalSessions", {
        value: override ? sessions.join(",") : "",
      }),
    onSuccess: onSaved,
  });

  return (
    <Card>
      <CardTitle right="empty → per-EA cfg wins">Global session override</CardTitle>
      <label className="flex items-center gap-3 text-sm">
        <input
          type="checkbox"
          checked={override}
          onChange={(e) => setOverride(e.target.checked)}
        />
        <span>Force a global session window across all EAs</span>
      </label>
      <fieldset className={`mt-3 flex gap-4 text-sm ${override ? "" : "opacity-40"}`}>
        {SESSIONS.map((s) => (
          <label key={s} className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={!override}
              checked={sessions.includes(s)}
              onChange={(e) =>
                setSessions((prev) =>
                  e.target.checked
                    ? Array.from(new Set([...prev, s]))
                    : prev.filter((x) => x !== s),
                )
              }
            />
            <span className="capitalize">{s}</span>
          </label>
        ))}
      </fieldset>
      <button
        type="button"
        className="btn-primary mt-4"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
      >
        {mutation.isPending ? "Saving…" : "Save"}
      </button>
    </Card>
  );
}

function parseSessions(csv: string | null): SessionKey[] {
  if (!csv) return ["asia", "europe", "us"];
  return csv
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter((s): s is SessionKey => SESSIONS.includes(s as SessionKey));
}

function SessionHoursCard({
  hours,
  onSaved,
}: {
  hours: Record<string, number>;
  onSaved: () => void;
}) {
  const [edit, setEdit] = useState(hours);
  useEffect(() => setEdit(hours), [hours]);

  const mutation = useMutation({
    mutationFn: () =>
      api.post("/admin/api/session-hours", {
        asiaStart: Number(edit.asiaStart),
        asiaEnd: Number(edit.asiaEnd),
        europeStart: Number(edit.europeStart),
        europeEnd: Number(edit.europeEnd),
        usStart: Number(edit.usStart),
        usEnd: Number(edit.usEnd),
      }),
    onSuccess: onSaved,
  });

  const rows: Array<[string, [string, string]]> = [
    ["Asia", ["asiaStart", "asiaEnd"]],
    ["Europe", ["europeStart", "europeEnd"]],
    ["US", ["usStart", "usEnd"]],
  ];

  return (
    <Card>
      <CardTitle right="UTC hours · 0-24">Session windows</CardTitle>
      <div className="-mx-5 overflow-x-auto px-5">
      <table className="w-full min-w-[480px] text-sm">
        <thead className="text-left text-xs uppercase tracking-wider text-muted">
          <tr>
            <th className="py-2">Session</th>
            <th>Start (UTC)</th>
            <th>End (UTC)</th>
            <th>VNT preview</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, [k1, k2]]) => (
            <tr key={label} className="border-t border-hairline-dark">
              <td className="py-2 font-medium">{label}</td>
              <td>
                <input
                  type="number"
                  min={0}
                  max={24}
                  value={edit[k1]}
                  onChange={(e) => setEdit((s) => ({ ...s, [k1]: Number(e.target.value) }))}
                  className="w-16 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono"
                />
              </td>
              <td>
                <input
                  type="number"
                  min={0}
                  max={24}
                  value={edit[k2]}
                  onChange={(e) => setEdit((s) => ({ ...s, [k2]: Number(e.target.value) }))}
                  className="w-16 rounded-md border border-hairline-dark bg-canvas-dark px-2 py-1 font-mono"
                />
              </td>
              <td className="font-mono text-xs text-muted">
                {utcToVnt(edit[k1])}h → {utcToVnt(edit[k2])}h VNT
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <button
        className="btn-primary mt-4"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
      >
        {mutation.isPending ? "Saving…" : "Save hours"}
      </button>
    </Card>
  );
}

function utcToVnt(h: number): number {
  if (h === 24) return 24;
  return (h + 7) % 24;
}

function ResetRealizedCard({
  anchorMs,
  onSaved,
}: {
  anchorMs: string | null;
  onSaved: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const anchor = anchorMs ? Number(anchorMs) : 0;
  // Anchor only matters within the current UTC day (EA takes max(midnight,
  // anchor)). Show it as active only if set today.
  const todayStartUtc = Date.UTC(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth(),
    new Date().getUTCDate(),
  );
  const active = anchor > todayStartUtc;

  const setMut = useMutation({
    mutationFn: (value: string) =>
      api.post("/admin/api/flags/realizedAnchorMs", { value }),
    onSuccess: () => {
      setConfirming(false);
      onSaved();
    },
  });

  return (
    <Card>
      <CardTitle right="affects every EA's realized counter">
        Reset realized counter
      </CardTitle>
      <p className="mb-3 text-sm text-muted">
        Sets a "count from now" anchor — every EA reports{" "}
        <code>realized today</code> / trade counts only for deals closed after
        this moment. Auto-clears at 00:00 UTC (07:00 VNT). Does not touch the
        broker; it only changes what the board counts.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {active ? (
          <Pill tone="warn">
            anchored {new Date(anchor).toISOString().slice(11, 16)} UTC
          </Pill>
        ) : (
          <Pill tone="neutral">no anchor (counting from 00:00 UTC)</Pill>
        )}
        {confirming ? (
          <>
            <span className="text-sm text-warn">
              Reset realized to 0 for all EAs from now?
            </span>
            <button
              type="button"
              className="rounded-md bg-trading-down/90 px-3 py-1.5 text-sm font-semibold text-on-primary hover:bg-trading-down disabled:opacity-50"
              disabled={setMut.isPending}
              onClick={() => setMut.mutate(String(Date.now()))}
            >
              {setMut.isPending ? "…" : "Confirm reset"}
            </button>
            <button
              type="button"
              className="rounded-md border border-hairline-dark px-3 py-1.5 text-sm text-on-dark"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            className="rounded-md border border-warn/40 px-3 py-1.5 text-sm font-semibold text-warn hover:bg-warn/10"
            onClick={() => setConfirming(true)}
          >
            Reset realized now
          </button>
        )}
        {active ? (
          <button
            type="button"
            className="text-xs text-muted underline hover:text-on-dark"
            onClick={() => setMut.mutate("0")}
            disabled={setMut.isPending}
          >
            clear anchor
          </button>
        ) : null}
      </div>
    </Card>
  );
}

function TelegramMutesCard({
  current,
  onSaved,
}: {
  current: string;
  onSaved: () => void;
}) {
  const initial = current
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const [muted, setMuted] = useState<string[]>(initial);
  useEffect(() => setMuted(initial), [current]);

  const known = useQuery({
    queryKey: ["indicators-known"],
    queryFn: () => api.get<{ types: string[] }>("/admin/api/indicators/known"),
  });

  const mutation = useMutation({
    mutationFn: () =>
      api.post("/admin/api/flags/telegramMutedIndicators", {
        value: muted.join(","),
      }),
    onSuccess: onSaved,
  });

  const toggle = (t: string): void => {
    setMuted((m) => (m.includes(t) ? m.filter((x) => x !== t) : [...m, t]));
  };

  const all = known.data?.types ?? [];

  return (
    <Card>
      <CardTitle right="Pine alerts still persist + EA still trades">
        Telegram mute
      </CardTitle>
      <p className="mb-3 text-xs text-muted">
        Tick to suppress Telegram for that indicator. Server still ingests the
        signal, EAs still poll and act on it — only the channel goes quiet.
      </p>
      {all.length === 0 ? (
        <div className="py-2 text-sm text-muted">Loading indicator list…</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {all.map((t) => {
            const on = muted.includes(t);
            return (
              <label
                key={t}
                className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition ${
                  on
                    ? "border-warn/40 bg-warn/10 text-warn"
                    : "border-hairline-dark text-on-dark hover:bg-surface-elevated/60"
                }`}
              >
                <input type="checkbox" checked={on} onChange={() => toggle(t)} />
                <span className="font-mono">{t}</span>
              </label>
            );
          })}
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          className="btn-primary"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Saving…" : "Save mute list"}
        </button>
        {mutation.isSuccess ? (
          <span className="text-sm text-trading-up">Saved.</span>
        ) : null}
        {mutation.error ? (
          <span className="text-sm text-trading-down">
            {(mutation.error as Error).message}
          </span>
        ) : null}
      </div>
    </Card>
  );
}

// Per-indicator anti-whipsaw cooldown. Stored as CSV in systemFlags:
//   `OCC_R5.1=180,ISB-live=120`
// Server's webhook accept() reads this before inserting a fresh BUY/SELL —
// if the prior OPPOSITE-direction entry from the same (indicator, symbol)
// is within the cooldown window, the new signal is silently dropped (no
// row, no synth-close, no EA action).
function FlipCooldownCard({
  current,
  onSaved,
}: {
  current: string;
  onSaved: () => void;
}) {
  const parseRows = (csv: string): Array<{ indicator: string; seconds: string }> =>
    csv
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((pair) => {
        const [k, v] = pair.split("=").map((s) => s.trim());
        return { indicator: k ?? "", seconds: v ?? "" };
      })
      .filter((r) => r.indicator);

  const [rows, setRows] = useState(parseRows(current));
  useEffect(() => setRows(parseRows(current)), [current]);

  const known = useQuery({
    queryKey: ["indicators-known"],
    queryFn: () => api.get<{ types: string[] }>("/admin/api/indicators/known"),
  });

  const mutation = useMutation({
    mutationFn: () => {
      const csv = rows
        .filter((r) => r.indicator && Number(r.seconds) > 0)
        .map((r) => `${r.indicator}=${Math.floor(Number(r.seconds))}`)
        .join(",");
      return api.post("/admin/api/flags/flipCooldownSeconds", { value: csv });
    },
    onSuccess: onSaved,
  });

  const allIndicators = known.data?.types ?? [];

  const setRow = (idx: number, patch: Partial<{ indicator: string; seconds: string }>) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx: number) =>
    setRows((rs) => rs.filter((_, i) => i !== idx));
  const addRow = () =>
    setRows((rs) => [...rs, { indicator: "", seconds: "" }]);

  return (
    <Card>
      <CardTitle right="server-side · drops signals BEFORE EA sees them">
        Flip cooldown (anti-whipsaw)
      </CardTitle>
      <p className="mb-3 text-xs text-muted">
        When an indicator fires a BUY/SELL opposite to its most recent entry
        for the same symbol within N seconds, the new signal is silently
        dropped — no DB row, no EA action, no Telegram. Use it to filter
        out fast-flip noise (e.g. <code>OCC_R5.1</code> flipping every 1-2
        m1 bars). Existing open positions stay open; broker SL/TP or the
        next non-throttled signal closes them. Same-direction repeats are
        not affected.
      </p>

      {rows.length === 0 ? (
        <div className="rounded-md border border-hairline-dark/60 bg-canvas-dark/40 px-3 py-4 text-center text-sm text-muted">
          No cooldowns set. Click "Add row" to start.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-2">
              <select
                className="rounded-md border border-hairline-dark bg-canvas-dark px-3 py-1.5 font-mono text-sm text-on-dark"
                value={r.indicator}
                onChange={(e) => setRow(idx, { indicator: e.target.value })}
              >
                <option value="">— pick indicator —</option>
                {allIndicators.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
                {r.indicator && !allIndicators.includes(r.indicator) ? (
                  <option value={r.indicator}>{r.indicator} (legacy)</option>
                ) : null}
              </select>
              <input
                type="number"
                min={1}
                max={3600}
                step={1}
                placeholder="seconds"
                className="w-24 rounded-md border border-hairline-dark bg-canvas-dark px-3 py-1.5 font-mono text-sm"
                value={r.seconds}
                onChange={(e) => setRow(idx, { seconds: e.target.value })}
              />
              <span className="text-xs text-muted">
                {r.seconds && Number(r.seconds) > 0
                  ? `≈ ${Math.round(Number(r.seconds) / 60 * 10) / 10} min`
                  : ""}
              </span>
              <button
                type="button"
                className="text-xs text-muted underline hover:text-trading-down"
                onClick={() => removeRow(idx)}
              >
                remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="rounded-md border border-hairline-dark px-3 py-1.5 text-sm text-on-dark hover:bg-surface-elevated"
          onClick={addRow}
        >
          + Add row
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Saving…" : "Save cooldowns"}
        </button>
        {mutation.isSuccess ? (
          <span className="text-sm text-trading-up">Saved.</span>
        ) : null}
        {mutation.error ? (
          <span className="text-sm text-trading-down">
            {(mutation.error as Error).message}
          </span>
        ) : null}
      </div>
    </Card>
  );
}

function ApiKeysCard({ keys, onChanged }: { keys: ApiKeyRow[]; onChanged: () => void }) {
  const [label, setLabel] = useState("");
  const [created, setCreated] = useState<{ label: string; token: string } | null>(null);

  const createMut = useMutation({
    mutationFn: () => api.post<{ id: number; label: string; token: string }>("/admin/api/keys", { label }),
    onSuccess: (data) => {
      setCreated({ label: data.label, token: data.token });
      setLabel("");
      onChanged();
    },
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      api.post(`/admin/api/keys/${id}/active`, { active }),
    onSuccess: onChanged,
  });

  return (
    <Card>
      <CardTitle right={`${keys.length} keys`}>EA API keys</CardTitle>
      {created ? (
        <div className="mb-4 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm">
          <div className="font-semibold text-primary">New key for `{created.label}`</div>
          <code className="mt-1 block break-all font-mono text-xs text-on-dark">{created.token}</code>
          <div className="mt-1 text-xs text-muted">
            Copy now — the token is shown ONCE. Plug it into the EA's `ServerAuthToken` input.
          </div>
          <button onClick={() => setCreated(null)} className="mt-2 text-xs text-muted underline">
            dismiss
          </button>
        </div>
      ) : null}
      <div className="-mx-5 overflow-x-auto px-5">
      <table className="w-full min-w-[560px] text-sm">
        <thead className="text-left text-xs uppercase tracking-wider text-muted">
          <tr>
            <th className="py-2">Label</th>
            <th>Created</th>
            <th>Last used</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id} className="border-t border-hairline-dark">
              <td className="py-2 font-mono">{k.label}</td>
              <td className="text-muted">{fmtTs(k.createdAt)}</td>
              <td className="text-muted">{k.lastUsedAt ? fmtTs(k.lastUsedAt) : "—"}</td>
              <td>
                <Pill tone={k.isActive ? "good" : "neutral"}>
                  {k.isActive ? "active" : "disabled"}
                </Pill>
              </td>
              <td className="text-right">
                <button
                  type="button"
                  className="text-xs text-muted hover:text-on-dark underline"
                  onClick={() =>
                    toggleMut.mutate({ id: k.id, active: !k.isActive })
                  }
                >
                  {k.isActive ? "disable" : "enable"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="EA label e.g. elvis-cent-xau"
          className="flex-1 rounded-md border border-hairline-dark bg-canvas-dark px-3 py-2 text-sm"
        />
        <button
          className="btn-primary"
          disabled={!label || createMut.isPending}
          onClick={() => createMut.mutate()}
        >
          {createMut.isPending ? "Creating…" : "Create key"}
        </button>
      </div>
    </Card>
  );
}
